import React, { useMemo, useState, useEffect } from 'react';
import { Button, makeStyles, styled, Box } from '@material-ui/core';
import { Email, BookmarkBorderOutlined, Bookmark } from '@material-ui/icons';
import { useIntl } from 'react-intl';

import { SdmRichText, SdmStack } from '..';
import translations from 'utils/translations';

import { Contents, Row, Container, Divider } from './Contents';
import { PageGeneratorThemeProvider, Typography, theme } from './mui-overrides';
import { Caption } from 'components/Caption';
import { noop } from 'utils/noop';
import { theme as MainTheme } from 'theme';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

const useStyles = makeStyles(() => ({
  textInfo: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    '@media (min-width:960px)': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  primaryButton: {
    textDecoration: 'underline',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  flexInfoBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginBottom: 20,
    '@media (min-width:960px)': {
      flexDirection: 'row',
    },
  },
  hideOnMobile: {
    '@media (max-width:959px)': {
      display: 'none',
    },
  },
  hideOnDesktop: {
    display: 'block',
    '@media (min-width:960px)': {
      display: 'none',
    },
  },
  overview: {
    ...theme.typography.overview,
    '& div[data-contents=true] > div[data-block=true] > div[data-offset-key]': {
      padding: 0,
      marginBottom: 10,
    },
    '& div[data-contents=true] > div[data-block=true]:last-of-type > div[data-offset-key]':
      {
        marginBottom: 0,
      },
    '& a': {
      color: MainTheme.colors.primary.red,
    },
  },
  captionContainer: {
    '@media screen and (min-width: 992px)': {
      marginTop: 45,
    },
    marginTop: 28,
  },
  cover: {
    ['@media (min-width: 992px)']: {
      marginBottom: 70,
    },
    marginBottom: 40,
  },
}));

const Image = styled('img')({
  maxWidth: '100%',
  height: 'auto',
});

export const ProductPageTemplate = ({
  name,
  brandName,
  cover,
  heroText,
  overview,
  contents = [],
  onContactUs = noop,
  onBookmarks = noop,
  bookmarks,
  onVisitBrandPage = noop,
  category = undefined,
  collection = undefined,
}) => {
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const classes = useStyles();

  const [fixed, setFixed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setFixed(true);
      } else {
        setFixed(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <PageGeneratorThemeProvider>
      <Container>
        <SdmStack>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            className={classes.actions}
            style={{
              position: fixed ? 'fixed' : 'static',
              top: fixed ? 0 : 'auto',
              left: 0,
              right: 0,
              zIndex: 999,
              backgroundColor: fixed ? 'white' : 'transparent',
              padding: fixed ? '10px 15px' : 0,
              [`@media (min-width:960px)`]: {
                justifyContent: 'flex-start',
                padding: fixed ? '10px 130px' : 0,
              },
            }}
          >
            <Button
              style={{
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                marginRight: '20px',
              }}
            >
              <KeyboardBackspaceIcon style={{ marginRight: '5px' }} />
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  color: 'black',
                  textDecoration: 'underline',
                }}
              >
                Prodotti
              </Typography>
            </Button>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{
                gap: '10px',
                [`@media (max-width:959px)`]: {
                  marginLeft: 'auto',
                },
              }}
            >
              <Button
                color="primary"
                variant="text"
                onClick={onVisitBrandPage}
                className={classes.primaryButton}
              >
                {i18n.visitBrandPage}
              </Button>
              <Button
                color="primary"
                variant="text"
                onClick={onContactUs}
                startIcon={<Email />}
                className={classes.primaryButton}
              >
                <span className={classes.hideOnMobile}>{i18n.contactUs}</span>
              </Button>
              <Button
                color="primary"
                variant="text"
                startIcon={
                  bookmarks ? <Bookmark /> : <BookmarkBorderOutlined />
                }
                onClick={onBookmarks}
                className={classes.primaryButton}
              >
                <span className={classes.hideOnMobile}>
                  {bookmarks ? i18n.removeFromBookmarks : i18n.addToBookmarks}
                </span>
              </Button>
            </Box>
          </Box>

          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 400,
              lineHeight: '48px',
              textAlign: 'center',
              marginBottom: 10,
              marginTop: 20,
              [`@media (min-width:960px)`]: {
                fontSize: '40px',
                lineHeight: '56px',
              },
            }}
          >
            {name}
          </Typography>
          <Box className={classes.flexInfoBox}>
            <Typography className={classes.textInfo}>
              {`Presentato da ${brandName}`}
            </Typography>
            <Typography
              className={`${classes.textInfo} ${classes.hideOnDesktop}`}
            >
              |
            </Typography>
            {category ? (
              <Typography className={classes.textInfo}>
                {`${i18n.categoryPreview}: ${category}`}
              </Typography>
            ) : null}
          </Box>

          {collection && collection?.length ? (
            <Typography
              variant="categoryPreview"
              style={{ marginBottom: 20 }}
            >{`${i18n.collectionPreview}: ${collection}`}</Typography>
          ) : null}

          <Row
            itemProps={{
              md: 10,
            }}
          >
            <Image src={cover.img} alt={cover.alt} />
            {heroText && (
              <div className={classes.captionContainer}>
                <Caption text={heroText} textAlign="center" variant="cover" />
              </div>
            )}
          </Row>
          <Row className={classes.captionContainer}>
            <SdmRichText
              editorClassName={classes.overview}
              variant="white"
              editorStyleOverride={{
                maxHeight: 'none',
                marginRight: 0,
                paddingLeft: 0,
                minHeight: 'none !important',
              }}
              formControlStyle={{
                margin: 0,
              }}
              labelStyle={{
                display: 'none',
              }}
              value={overview}
              readOnly
            />
          </Row>
          <Row>
            <Divider />
          </Row>
        </SdmStack>
      </Container>
      <SdmStack>
        <Contents contents={contents} />
      </SdmStack>
    </PageGeneratorThemeProvider>
  );
};

export function useProductDefaultValue() {
  return useMemo(() => {
    return {
      name: 'Product',
      brandName: 'Brand',
      cover: {
        img: 'https://via.placeholder.com/1200x900',
        alt: '1200x900',
      },
      overview: {
        blocks: [
          {
            key: (Math.random() * 10000).toFixed(0),
            text: '',
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
        entityMap: {},
      },
    };
  }, []);
}
